/* eslint-disable react/jsx-props-no-spreading */
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// Utils
import groupOptionsByOptGroup from '../../../utils/optGroup/optGroup';

// Domain
import {
  getSingleValueField,
  getMultipleValueField,
  createSingleValueOnChange,
} from '../../../domain/tableConditionalFilter/conditionalFiltersUseCase';

// Components
import TextField from '../../molecules/Fields/TextField/TextField';
import DateTimeField from '../../molecules/Fields/DateTimeField/DateTimeField';
import SelectField from '../../atoms/Select/Select';
import SelectMultipleCheckboxList from '../../molecules/SelectMultipleCheckboxList/SelectMultipleCheckboxList';
import ConditionalFiltersDateRange from './ConditionalFiltersDateRange';

/**
 * Render input based on type
 * @param {object} props
 * @param {import('react-hook-form').Control<any>} props.controlRhf - React Hook Form control
 * @param {ApiManagerGlobalFiltersCreateValue} props.field - Field of the input
 * @param {string} props.name - Name of the input
 * @param {Record<string, ConditionalFilter>} props.filtersById - Filters by ID
 * @param {boolean} [props.isClearable] - Allow clearing single select values
 * @returns {JSX.Element | null} ConditionalFiltersRender component
 */
function ConditionalFiltersRender({
  controlRhf, field, name, filtersById, isClearable = false,
}) {
  const { id, operator } = field;
  const type = filtersById?.[id]?.type;
  if (!id || !type) return null;

  const { t } = useTranslation();

  switch (type) {
    case 'date':
    case 'time': {
      return (
        <Controller
          control={controlRhf}
          name={name}
          render={({ field: { ref, ...fieldInput }, fieldState: { error } }) => {
            const value = getSingleValueField(fieldInput.value);
            return (
              <DateTimeField
                disabled={!operator}
                type={type}
                error={t(error?.message || '')}
                {...fieldInput}
                value={value}
                onChange={createSingleValueOnChange(fieldInput.onChange)}
              />
            );
          }}
        />
      );
    }

    case 'dateRange': {
      return (
        <Controller
          control={controlRhf}
          name={name}
          render={({ field: { ref, ...fieldInput }, fieldState: { error } }) => (
            <ConditionalFiltersDateRange
              id={name}
              value={getMultipleValueField(fieldInput.value)}
              onChange={fieldInput.onChange}
              disabled={!operator}
              error={t(error?.message || '')}
            />
          )}
        />
      );
    }

    case 'select': {
      const filterWithOptions = /** @type {ApiManagerFiltersSelect} */ (filtersById?.[id] ?? {});

      const optionsGrouped = groupOptionsByOptGroup(filterWithOptions?.valueOptions).map((group) => ({
        label: group.lib,
        options: group.options.map((option) => ({
          value: option.id,
          label: option.lib,
          color: option.color,
        })),
      }));
      return (
        <Controller
          control={controlRhf}
          name={name}
          render={({ field: { ref, ...fieldInput }, fieldState: { error } }) => {
            const value = getSingleValueField(fieldInput.value);
            return (
              <SelectField
                id={name}
                disabled={!operator}
                options={optionsGrouped}
                placeholder={t('table.filters.selectEnum')}
                error={t(error?.message || '')}
                isClearable={isClearable}
                {...fieldInput}
                value={value}
                onChange={(newValue) => {
                  if (!newValue) {
                    createSingleValueOnChange(fieldInput.onChange)('');
                    return;
                  }
                  createSingleValueOnChange(fieldInput.onChange)(newValue.value);
                }}
              />
            );
          }}
        />
      );
    }

    case 'select_multiple': {
      const filterWithOptions = /** @type {ApiManagerFiltersSelectMultiple} */ (filtersById?.[id] ?? {});

      const optionsGrouped = groupOptionsByOptGroup(filterWithOptions?.valueOptions).map((group) => ({
        label: group.lib,
        options: group.options.map((option) => ({
          value: option.id,
          label: option.lib,
          color: option.color,
        })),
      }));

      return (
        <Controller
          control={controlRhf}
          name={name}
          render={({ field: { ref, ...fieldInput }, fieldState: { error } }) => {
            const value = getMultipleValueField(fieldInput.value);
            return (
              <SelectMultipleCheckboxList
                id={name}
                optionsGrouped={optionsGrouped}
                value={value}
                onChange={fieldInput.onChange}
                disabled={!operator}
                error={t(error?.message || '')}
              />
            );
          }}
        />
      );
    }

    default:
      return (
        <Controller
          control={controlRhf}
          name={name}
          render={({ field: { ref, ...fieldInput }, fieldState: { error } }) => {
            const value = getSingleValueField(fieldInput.value);
            return (
              <TextField
                disabled={!operator}
                error={t(error?.message || '')}
                placeholder={t('table.filters.text')}
                {...fieldInput}
                value={value}
                onChange={createSingleValueOnChange(fieldInput.onChange)}
              />
            );
          }}
        />
      );
  }
}

export default ConditionalFiltersRender;
